/**
 * Date utility functions for handling dates in the booking system
 */

/**
 * Formats a Date object into a consistent string format (YYYY-MM-DD) for use as object keys
 * Ensures the date is formatted according to local timezone, not UTC
 * @param {Date} date - The date to format
 * @returns {string} Formatted date string in YYYY-MM-DD format
 */
export const formatLocalDateKey = (date) => {
  if (!date) return null;

  // Ensure we have a Date object
  const dateObj = date instanceof Date ? date : new Date(date);

  // Get year, month, and day in local timezone
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0"); // Months are 0-indexed
  const day = String(dateObj.getDate()).padStart(2, "0");

  // Format as YYYY-MM-DD
  return `${year}-${month}-${day}`;
};

/**
 * Checks if a date falls on a weekday that's marked as unavailable
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {Object} availableWeekdays - Object with weekday keys and boolean values
 * @returns {boolean} True if the date is on an unavailable weekday
 */
export const isNonAvailableWeekday = (dateStr, availableWeekdays) => {
  if (!dateStr || !availableWeekdays) return false;

  // Skip this check for the special "weekday-settings" key
  if (dateStr === "weekday-settings") return false;

  const date = new Date(dateStr);
  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Map day number to weekday name
  const weekdayNames = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const weekdayName = weekdayNames[dayOfWeek];

  // If the weekday is explicitly set to false, or if it's undefined/null
  return availableWeekdays[weekdayName] === false;
};

/**
 * Formats a date for display in French locale
 * @param {string|Date} date - Date to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDateForDisplay = (date, options = {}) => {
  if (!date) return "";

  const dateObj = date instanceof Date ? date : new Date(date);

  const defaultOptions = {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  return dateObj.toLocaleDateString("fr-FR", { ...defaultOptions, ...options });
};
